export const defaultFeatureFlags = Object.freeze({
  debugStaticGraph: false,
});

export const completedStatuses = new Set(['completed', 'needs_human_review']);
export const terminalStatuses = new Set(['completed', 'needs_human_review', 'failed']);
export const allowedListTypes = [
  'shopping',
  'packing',
  'school_supplies',
  'travel',
  'checklist',
  'custom',
];
export const taskStatuses = ['open', 'in_progress', 'done', 'cancelled'];
export const taskPriorities = ['low', 'medium', 'high', 'urgent'];
export const calendarStatuses = ['scheduled', 'completed', 'cancelled'];
export const navigationTargetScreens = new Set([
  'lists',
  'tasks',
  'calendar',
  'family',
  'family_invites',
]);
export const initialListForm = {
  name: 'School Supplies List',
  type: 'school_supplies',
};
export const initialListItemForm = {
  name: '',
  quantity: '1',
  unit: '',
  notes: '',
  group: 'school_supplies',
};
export const initialTaskForm = {
  title: '',
  description: '',
  assignedTo: '',
  dueDate: '',
  priority: 'medium',
  tags: 'school',
};
export const initialEventForm = {
  title: '',
  description: '',
  location: '',
  startAt: '',
  endAt: '',
  allDay: false,
  tags: 'school',
};

export function normalizeFeatureFlags(flags) {
  return {
    ...defaultFeatureFlags,
    debugStaticGraph: Boolean(flags?.debugStaticGraph),
  };
}

export function buildCurrentMonthRange() {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999)
  );

  return {
    startFrom: start.toISOString(),
    startTo: end.toISOString(),
  };
}

export function normalizeMutationResult(payload) {
  if (!payload || Array.isArray(payload)) {
    return null;
  }

  return payload.result || payload.data?.result || payload.data || payload;
}

export function extractArrayFromPayload(payload, keys) {
  if (Array.isArray(payload)) {
    return payload;
  }

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }

    if (Array.isArray(payload?.data?.[key])) {
      return payload.data[key];
    }
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
}

export function normalizeLists(payload) {
  return extractArrayFromPayload(payload, ['lists']);
}

export function normalizeListItems(payload) {
  return extractArrayFromPayload(payload, ['currentList', 'items']);
}

export function normalizeTasks(payload) {
  return extractArrayFromPayload(payload, ['tasks']);
}

export function normalizeCalendarEvents(payload) {
  return extractArrayFromPayload(payload, ['events']);
}

export function normalizeNavigationTarget(target) {
  if (!target || typeof target !== 'object' || Array.isArray(target)) {
    return null;
  }

  if (!navigationTargetScreens.has(target.screen)) {
    return null;
  }

  return {
    screen: target.screen,
    params:
      target.params && typeof target.params === 'object' && !Array.isArray(target.params)
        ? target.params
        : {},
  };
}

export function buildNavigationTargetPath(target) {
  const navigationTarget = normalizeNavigationTarget(target);
  if (!navigationTarget) {
    return null;
  }

  if (navigationTarget.screen === 'lists') {
    const listId =
      navigationTarget.params?.listId ||
      navigationTarget.params?.focusListId ||
      '';

    if (!listId) {
      return '/lists';
    }

    const scope =
      navigationTarget.params?.scope === 'family' ||
      navigationTarget.params?.listType === 'family'
        ? 'family'
        : 'personal';

    const query = new URLSearchParams();
    if (navigationTarget.params?.focusItemId) {
      query.set('focusItemId', String(navigationTarget.params.focusItemId));
    }

    return `/lists/${scope}/${encodeURIComponent(String(listId))}${
      query.toString() ? `?${query}` : ''
    }`;
  }

  if (navigationTarget.screen === 'tasks') {
    const query = new URLSearchParams();
    if (navigationTarget.params?.focusTaskId) {
      query.set('focusTaskId', String(navigationTarget.params.focusTaskId));
    }
    if (navigationTarget.params?.view) {
      query.set('view', String(navigationTarget.params.view));
    }
    return `/tasks${query.toString() ? `?${query}` : ''}`;
  }

  if (navigationTarget.screen === 'calendar') {
    const query = new URLSearchParams();
    if (navigationTarget.params?.focusEventId) {
      query.set('focusEventId', String(navigationTarget.params.focusEventId));
    }
    return `/calendar${query.toString() ? `?${query}` : ''}`;
  }

  if (navigationTarget.screen === 'family') {
    const query = new URLSearchParams();
    if (navigationTarget.params?.focusMemberUserId) {
      query.set('tab', 'members');
      query.set('focusMemberUserId', String(navigationTarget.params.focusMemberUserId));
    }
    return `/family${query.toString() ? `?${query}` : ''}`;
  }

  if (navigationTarget.screen === 'family_invites') {
    const query = new URLSearchParams();
    query.set('tab', 'invites');
    if (navigationTarget.params?.focusInviteId) {
      query.set('focusInviteId', String(navigationTarget.params.focusInviteId));
    }
    if (navigationTarget.params?.view) {
      query.set('view', String(navigationTarget.params.view));
    }
    return `/family?${query}`;
  }

  return `/${navigationTarget.screen}`;
}

export function parseCommaSeparatedList(value) {
  if (!hasDisplayValue(value)) {
    return [];
  }

  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function normalizeQueue(payload) {
  if (!payload || Array.isArray(payload)) {
    return null;
  }

  return payload.queue || payload.data?.queue || payload.data || payload;
}

export function normalizeQueues(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.queues)) {
    return payload.queues;
  }

  if (Array.isArray(payload?.data?.queues)) {
    return payload.data.queues;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
}

export function normalizeJob(payload) {
  if (!payload || Array.isArray(payload)) {
    return null;
  }

  return payload.job || payload.data?.job || payload.data || payload;
}

export function getListItemId(item) {
  return item?._id || item?.id || item?.itemId || '';
}

export function getTaskId(task) {
  return task?._id || task?.id || task?.taskId || '';
}

export function getEventId(event) {
  return event?._id || event?.id || event?.eventId || '';
}

export function extractQueueOutput(queue) {
  return queue?.output || queue?.result || queue?.raw?.output || null;
}

export function extractQueuePayload(queue) {
  return queue?.payload || queue?.raw?.payload || null;
}

export function extractQueueInputFile(queue) {
  return queue?.inputFile || queue?.raw?.inputFile || null;
}

export function extractQueueDebugGraph(queue) {
  return queue?.debug?.graph || queue?.raw?.debug?.graph || null;
}

export function extractQueueDebugProgress(queue) {
  return queue?.debug?.progress || queue?.raw?.debug?.progress || null;
}

// The persisted trace stream is the authoritative, uncapped event record (with
// sequence + source); progress.events is a capped mirror kept for compatibility.
export function extractQueueDebugTrace(queue) {
  const trace = queue?.debug?.trace || queue?.raw?.debug?.trace || null;
  return Array.isArray(trace) && trace.length > 0 ? trace : null;
}

export function extractReferenceDocumentUrl(queue) {
  return queue?.storage?.blobUrl || queue?.raw?.storage?.blobUrl || '';
}

export function extractReferenceDocumentName(queue) {
  const blobName =
    queue?.storage?.blobName || queue?.raw?.storage?.blobName || '';

  if (!blobName) {
    return 'Reference document';
  }

  const parts = blobName.split('/');
  return parts[parts.length - 1] || 'Reference document';
}

export function getReviewQuestions(queue) {
  const output = extractQueueOutput(queue);
  const reviewQuestions = output?.reviewQuestions || [];
  if (reviewQuestions.length > 0) {
    return reviewQuestions;
  }

  if (queue?.status !== 'needs_human_review' || !hasDisplayValue(output?.humanInLoopQuestion)) {
    return [];
  }

  return [
    {
      id: 'legacy-human-in-loop-question',
      prompt: output.humanInLoopQuestion,
      responseStyle: 'open_ended',
      optional: false,
      reason: 'The pipeline is waiting for your choice before it can finish.',
      isLegacyHumanInLoopQuestion: true,
    },
  ];
}

export function getReviewAnswers(queue) {
  return extractQueueOutput(queue)?.reviewAnswers || [];
}

export function getInitialReviewResponses(queue) {
  const responses = {};
  const reviewQuestions = getReviewQuestions(queue);
  const reviewAnswers = getReviewAnswers(queue);

  reviewQuestions.forEach((question) => {
    const existing = reviewAnswers.find(
      (answer) => answer.questionId === question.id
    );

    if (!existing) {
      return;
    }

    const selectedOptions = existing.selectedOptions || [];
    const answerText = existing.answerText || '';
    responses[question.id] = {
      selectedOptions,
      answerText,
      ignored:
        selectedOptions.includes('Ignore') || answerText.trim() === 'Ignore',
    };
  });

  return responses;
}

export function isQuestionAnswered(question, response) {
  if (!response) {
    return false;
  }

  if (response.ignored) {
    return true;
  }

  if (question.responseStyle === 'multiple_choice') {
    return (response.selectedOptions || []).length > 0;
  }

  return Boolean(response.answerText?.trim());
}

export function hasPendingRequiredReviewQuestions(queue, responses) {
  if (queue?.status !== 'needs_human_review') {
    return false;
  }

  const reviewQuestions = getReviewQuestions(queue);

  return reviewQuestions.some((question) => {
    if (question.optional) {
      return false;
    }

    return !isQuestionAnswered(question, responses[question.id]);
  });
}

export function formatList(items) {
  if (!Array.isArray(items) || !items.length) {
    return 'None';
  }

  return items
    .map((item) =>
      typeof item === 'string'
        ? item
        : item.title || item.prompt || item.detail || JSON.stringify(item)
    )
    .join(', ');
}

export function hasDisplayValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return Boolean(value.trim());
  }

  return value !== null && value !== undefined && value !== '';
}

export function formatFileSize(sizeBytes) {
  if (typeof sizeBytes !== 'number' || Number.isNaN(sizeBytes) || sizeBytes < 0) {
    return 'Unknown';
  }

  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }

  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function normalizeProgress(progress) {
  if (!progress) {
    return null;
  }

  return {
    currentStage:
      progress.currentStage || progress.stage || progress.label || null,
    percent:
      typeof progress.percent === 'number' && Number.isFinite(progress.percent)
        ? progress.percent
        : 0,
    events: Array.isArray(progress.events) ? progress.events : [],
  };
}

export function formatPercent(percent) {
  if (typeof percent !== 'number' || Number.isNaN(percent)) {
    return '0%';
  }

  return `${Math.max(0, Math.min(100, Math.round(percent)))}%`;
}

export function describeProgressEvent(event) {
  if (typeof event === 'string') {
    return event;
  }

  return (
    event?.label ||
    event?.message ||
    event?.stage ||
    event?.name ||
    event?.type ||
    JSON.stringify(event)
  );
}

export function describeProgressEventMeta(event) {
  if (!event || typeof event === 'string') {
    return '';
  }

  const parts = [event.status, event.timestamp || event.at || event.createdAt].filter(Boolean);
  return parts.join(' | ');
}

export function summarizeText(value, maxLength = 140) {
  if (!hasDisplayValue(value)) {
    return '';
  }

  const normalized = String(value).trim().replace(/\s+/g, ' ');
  if (normalized.length <= maxLength) {
    return normalized;
  }

  if (maxLength <= 3) {
    return normalized.slice(0, maxLength);
  }

  return `${normalized.slice(0, maxLength - 3)}...`;
}

export function revokePreviewObjectUrl(preview) {
  if (preview?.objectUrl) {
    URL.revokeObjectURL(preview.objectUrl);
  }
}

export function getQuestionTriggers(queue) {
  const triggers = extractQueueOutput(queue)?.triggers;
  if (!Array.isArray(triggers)) {
    return [];
  }

  return triggers.filter((trigger) => {
    if (typeof trigger === 'string') {
      return true;
    }

    return trigger?.type === 'question' || trigger?.requiresUserInput;
  });
}

export function extractQueueRoute(queue) {
  return (
    extractQueueOutput(queue)?.routing?.pipeline ||
    extractQueueOutput(queue)?.copilot_response?.pipeline ||
    extractQueueDebugGraph(queue)?.pipeline ||
    ''
  );
}

export function extractQueueFailureMessage(queue) {
  return (
    queue?.error?.message ||
    queue?.error ||
    queue?.failureReason ||
    queue?.raw?.error?.message ||
    queue?.raw?.error ||
    extractQueueOutput(queue)?.error ||
    extractQueueOutput(queue)?.failureReason ||
    ''
  );
}

export function extractQueueExecutionNavigationTargets(queue) {
  const executions = extractQueueOutput(queue)?.action_resolution_pipeline?.executions;
  if (!Array.isArray(executions)) {
    return [];
  }

  return executions
    .map((execution, index) => {
      const navigationTarget = normalizeNavigationTarget(execution?.navigationTarget);
      if (!navigationTarget) {
        return null;
      }

      return {
        id: execution?.id || execution?.executionId || `execution-${index}`,
        label:
          execution?.action ||
          execution?.toolName ||
          execution?.name ||
          execution?.type ||
          `Execution ${index + 1}`,
        summary:
          execution?.message ||
          execution?.summary ||
          execution?.result?.message ||
          '',
        navigationTarget,
      };
    })
    .filter(Boolean);
}

export function canReviewQueue(queue) {
  return queue?.status === 'needs_human_review' && getReviewQuestions(queue).length > 0;
}

export function getQueueScreenMode(queue) {
  if (queue?.status === 'completed') {
    return 'final';
  }

  if (queue?.status === 'needs_human_review') {
    return 'review';
  }

  if (queue?.status === 'failed') {
    return 'failed';
  }

  return 'processing';
}


